import { createApi } from "@reduxjs/toolkit/query/react";

import {
  baseQuery,
  CREATE_CUSTOMS_BATCH,
  PROCESS_CUSTOMS_BATCH,
  GET_CUSTOMS_BATCH,
  GET_CUSTOMS_BATCHES,
  GET_CUSTOMS_DOCUMENTS,
  GET_CUSTOMS_DOCUMENT,
  GET_CUSTOMS_CLUSTERS,
  SEARCH_CUSTOMS_DOCUMENTS,
  DELETE_CUSTOMS_DOCUMENT,
  GET_CLUSTER_AUDIT,
  GET_CLUSTER_AUDITS,
  RUN_CLUSTER_AUDIT,
} from "@/graphql";

export const customsDocumentApi = createApi({
  reducerPath: "api/customsDocument",
  tagTypes: ["CustomsBatches", "CustomsDocuments", "CustomsClusters", "ClusterAudits"],
  baseQuery,
  endpoints: (builder) => ({
    // Create a new batch with pre-signed upload URLs
    createCustomsBatch: builder.mutation<
      CreateCustomsBatchResponse["createCustomsBatch"],
      CreateCustomsBatchInput
    >({
      query: (input) => ({
        document: CREATE_CUSTOMS_BATCH,
        variables: { input },
      }),
      transformResponse: (response: CreateCustomsBatchResponse) => {
        return response.createCustomsBatch;
      },
      invalidatesTags: ["CustomsBatches"],
    }),

    // Manually trigger processing for all UPLOADED documents in a batch
    processCustomsBatch: builder.mutation<
      ProcessCustomsBatchResponse["processCustomsBatch"],
      { batchId: string; tenantId?: string }
    >({
      query: ({ batchId, tenantId }) => ({
        document: PROCESS_CUSTOMS_BATCH,
        variables: { batchId, tenantId },
      }),
      transformResponse: (response: ProcessCustomsBatchResponse) => {
        return response.processCustomsBatch;
      },
      invalidatesTags: ["CustomsBatches", "CustomsDocuments"],
    }),

    // Get batch status with all documents
    getCustomsBatch: builder.query<
      CustomsBatchResponse["customsBatch"],
      string
    >({
      query: (batchId) => ({
        document: GET_CUSTOMS_BATCH,
        variables: { batchId },
      }),
      transformResponse: (response: CustomsBatchResponse) => {
        return response.customsBatch;
      },
      providesTags: ["CustomsBatches"],
    }),

    // List batches for tenant
    getCustomsBatches: builder.query<
      CustomsBatchesResponse["customsBatches"],
      {
        tenantId?: string;
        clientId?: string;
        pageNo?: number;
        pageSize?: number;
      }
    >({
      query: (variables) => ({
        document: GET_CUSTOMS_BATCHES,
        variables,
      }),
      transformResponse: (response: CustomsBatchesResponse) => {
        return response.customsBatches;
      },
      providesTags: ["CustomsBatches"],
    }),

    // List documents with filters
    getCustomsDocuments: builder.query<
      CustomsDocumentsResponse["customsDocuments"],
      {
        tenantId?: string;
        clientId?: string;
        batchId?: string;
        status?: CustomsDocumentStatus;
        clusterId?: string;
        pageNo?: number;
        pageSize?: number;
      }
    >({
      query: (variables) => ({
        document: GET_CUSTOMS_DOCUMENTS,
        variables,
      }),
      transformResponse: (response: CustomsDocumentsResponse) => {
        return response.customsDocuments;
      },
      providesTags: ["CustomsDocuments"],
    }),

    // Get single document with download URL
    getCustomsDocument: builder.query<
      CustomsDocumentResponse["customsDocument"],
      { docId: string; tenantId?: string }
    >({
      query: ({ docId, tenantId }) => ({
        document: GET_CUSTOMS_DOCUMENT,
        variables: { docId, tenantId },
      }),
      transformResponse: (response: CustomsDocumentResponse) => {
        return response.customsDocument;
      },
      providesTags: ["CustomsDocuments"],
    }),

    // List clusters with pagination and optional filters
    // Use 'identifier' to search by any identifier type (BL, Container, PO, Invoice, Entry)
    getCustomsClusters: builder.query<
      CustomsClustersResponse["customsClusters"],
      {
        tenantId?: string;
        clientId?: string;
        entryNumber?: string;
        identifier?: string;
        pageNo?: number;
        pageSize?: number;
      }
    >({
      query: (variables) => ({
        document: GET_CUSTOMS_CLUSTERS,
        variables,
      }),
      transformResponse: (response: CustomsClustersResponse) => {
        return response.customsClusters;
      },
      providesTags: ["CustomsClusters"],
    }),

    // Search documents by identifier or text
    searchCustomsDocuments: builder.query<
      CustomsDocumentsResponse["customsDocuments"],
      {
        query: string;
        tenantId?: string;
        clientId?: string;
        documentType?: CustomsDocumentType;
        pageNo?: number;
        pageSize?: number;
      }
    >({
      query: (variables) => ({
        document: SEARCH_CUSTOMS_DOCUMENTS,
        variables,
      }),
      transformResponse: (response: CustomsDocumentsResponse) => {
        return response.customsDocuments;
      },
      providesTags: ["CustomsDocuments"],
    }),

    // Delete a customs document
    deleteCustomsDocument: builder.mutation<
      boolean,
      { docId: string; tenantId?: string }
    >({
      query: ({ docId, tenantId }) => ({
        document: DELETE_CUSTOMS_DOCUMENT,
        variables: { docId, tenantId },
      }),
      transformResponse: (response: { deleteCustomsDocument: boolean }) => {
        return response.deleteCustomsDocument;
      },
      invalidatesTags: ["CustomsDocuments", "CustomsBatches"],
    }),

    // Get the most recent audit for a cluster
    getClusterAudit: builder.query<
      ClusterAuditResponse["clusterAudit"],
      { clusterId: string; tenantId?: string }
    >({
      query: ({ clusterId, tenantId }) => ({
        document: GET_CLUSTER_AUDIT,
        variables: { clusterId, tenantId },
      }),
      transformResponse: (response: ClusterAuditResponse) => {
        return response.clusterAudit;
      },
      providesTags: ["ClusterAudits"],
    }),

    // List audits with filters
    getClusterAudits: builder.query<
      ClusterAuditsResponse["clusterAudits"],
      {
        tenantId?: string;
        clientId?: string;
        status?: ClusterAuditStatus;
        pageNo?: number;
        pageSize?: number;
      }
    >({
      query: (variables) => ({
        document: GET_CLUSTER_AUDITS,
        variables,
      }),
      transformResponse: (response: ClusterAuditsResponse) => {
        return response.clusterAudits;
      },
      providesTags: ["ClusterAudits"],
    }),

    // Run AI audit on a cluster
    runClusterAudit: builder.mutation<
      RunClusterAuditResponse["runClusterAudit"],
      { clusterId: string; tenantId?: string }
    >({
      query: ({ clusterId, tenantId }) => ({
        document: RUN_CLUSTER_AUDIT,
        variables: { clusterId, tenantId },
      }),
      transformResponse: (response: RunClusterAuditResponse) => {
        return response.runClusterAudit;
      },
      invalidatesTags: ["ClusterAudits"],
    }),
  }),
});

export const {
  useCreateCustomsBatchMutation,
  useProcessCustomsBatchMutation,
  useGetCustomsBatchQuery,
  useLazyGetCustomsBatchQuery,
  useGetCustomsBatchesQuery,
  useGetCustomsDocumentsQuery,
  useGetCustomsDocumentQuery,
  useLazyGetCustomsDocumentQuery,
  useGetCustomsClustersQuery,
  useLazyGetCustomsClustersQuery,
  useSearchCustomsDocumentsQuery,
  useLazySearchCustomsDocumentsQuery,
  useDeleteCustomsDocumentMutation,
  useGetClusterAuditQuery,
  useLazyGetClusterAuditQuery,
  useGetClusterAuditsQuery,
  useRunClusterAuditMutation,
} = customsDocumentApi;
